import { useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { useConfig } from "@/api/hooks/useConfig";
import { CardView } from "@/components/v2/CardView";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import { useContext, useMemo, useState } from "react";

import { HomePageContext } from "../../context";
import { WasteStreamDistributionGraph } from "./components/graph";
import { WasteStreamDistributionLegend } from "./components/legend";
import { getWasteTypeDistributionData } from "./utils";

export const WasteStreamDistribution: React.FC = () => {
  const { t } = useTrans();
  const { timeFilter, selectedTenantCategoryIds } = useContext(HomePageContext);
  const { currentTerminal } = useTerminalsState();
  const { wasteTypes } = useCommonEntitiesStore();
  const { config } = useConfig();
  const { wasteTypeConfig, terminalConfig } = useConfigService();

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const { data, isLoading } = trpc.dwh.terminals.getById.useQuery(
    {
      ...(timeFilter || defaultTimeFilter),
      terminalId: currentTerminal?.id || "",
      tenantCategories: selectedTenantCategoryIds || [],
    },
    {
      queryKeyHashFn: () =>
        [
          "terminal",
          currentTerminal?.id,
          timeFilter?.startTimeISO,
          timeFilter?.endTimeISO,
          selectedTenantCategoryIds,
        ].toString(),
      enabled: !!timeFilter && !!currentTerminal,
    },
  );

  const otherColor = useMemo(
    () => terminalConfig?.otherColor || config.otherColor || "#B2ABAB",
    [terminalConfig, config],
  );

  const wasteTypeDistributionData = useMemo(
    () =>
      getWasteTypeDistributionData(
        data?.terminal,
        wasteTypes,
        t("common:unknown"),
        t("homePage:wasteDistributionOther"),
        wasteTypeConfig,
        otherColor,
      ),
    [wasteTypes, data, wasteTypeConfig, otherColor, t],
  );

  return (
    <CardView
      title="homePage:wasteDistributionTitle"
      subtitle="homePage:wasteDistributionSubtitle"
    >
      <ViewWithLoader isLoading={isLoading}>
        <div className="mt-3 flex flex-1 flex-wrap items-center justify-center gap-[24px]">
          <WasteStreamDistributionGraph
            data={wasteTypeDistributionData}
            hoverIndex={hoverIndex}
            setHoverIndex={setHoverIndex}
          />

          <WasteStreamDistributionLegend
            data={wasteTypeDistributionData}
            hoverIndex={hoverIndex}
            setHoverIndex={setHoverIndex}
          />
        </div>
      </ViewWithLoader>
    </CardView>
  );
};
